import { Button, Col, message, Row, Space, Switch, Table, Tooltip } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { isStoreAliasFirst, useStoreNameConfig } from 'hooks/business/store/useStoreName';
import toLowerCase, { toUpperCase } from 'utils/toLowerCase';
import { css } from '@emotion/css';
import { JustRightHeightModal } from 'components/styled/scoped';
import { useTransAppProductName } from 'hooks/useTrans';
import { useTranslate } from '@tolgee/react';
import { useGetRegionLabel } from 'hooks/business/useRegion';

interface Props {
  platform: General.PlatformUpperCase;
  regionList: General.Region[];
}

const shopAliasSettingCls = css`
  max-height: 80vh;
  overflow: hidden;
  .ant-modal-title {
    text-transform: capitalize;
  }
  .ant-modal-body {
    max-height: calc(80vh - 105px);
    overflow: auto;
  }
  .ant-modal-footer {
    height: 56px;
  }
`;
const ShopAliasSetting = (props: Props) => {
  const { platform, regionList } = props;
  const [visible, setVisible] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const { config, updateConfig, createConfig } = useStoreNameConfig();
  const { t } = useTranslate();
  const getRegionLabel = useGetRegionLabel();
  const appProductNameT = useTransAppProductName();

  const dataSource = useMemo(() => {
    return regionList.map((x) => {
      const target = config?.find(
        (conf) => conf.region === toUpperCase(x) && conf.platform === toUpperCase(platform),
      );
      return {
        region: x,
        aliasFlag: isStoreAliasFirst(config, platform, x),
        id: target?.id,
      };
    });
  }, [regionList, platform, config]);
  const onChange = useCallback(
    (item: (typeof dataSource)[number], checked: boolean) => {
      if (item.id) {
        return updateConfig(item.id, checked);
      }
      return createConfig({
        region: toUpperCase(item.region),
        platform: toUpperCase(platform),
        aliasFlag: checked,
      });
    },
    [createConfig, platform, updateConfig],
  );

  const columns = useMemo(() => {
    return [
      {
        title: t('field.region'),
        dataIndex: 'region',
        render(region: General.Region) {
          return getRegionLabel(region);
        },
      },
      {
        title: () => (
          <Space>
            <span>{t('ViewShops.店铺别名展示')}</span>
            <Tooltip title={appProductNameT('ViewShops.storeAliasOpenTips')}>
              <QuestionCircleOutlined />
            </Tooltip>
          </Space>
        ),
        dataIndex: 'aliasFlag',
        render(value: boolean, record: (typeof dataSource)[number]) {
          return <Switch checked={value} onChange={(checked) => onChange(record, checked)} />;
        },
      },
    ];
  }, [onChange, appProductNameT, t, getRegionLabel]);
  const onBatchChange = useCallback(
    (checked: boolean) => {
      const target = dataSource.filter((x) => selectedRowKeys.includes(x.region));
      Promise.all(target.map((item) => onChange(item, checked)))
        .then(() => {
          message.success(checked ? t('ViewShops.批量开启成功') : t('ViewShops.批量关闭成功'));
          setSelectedRowKeys([]);
        })
        .catch(() => {
          message.error(t('ViewShops.批量设置错误请重试'));
        });
    },
    [dataSource, onChange, selectedRowKeys, t],
  );
  const rowSelection = useMemo(() => {
    return {
      selectedRowKeys,
      onChange: (_: any, selected: typeof dataSource) => {
        setSelectedRowKeys(selected.map((x) => x.region));
      },
    };
  }, [selectedRowKeys]);
  const footer = () => {
    if (!selectedRowKeys.length) {
      return null;
    }
    return (
      <Row justify="space-between">
        <Col>
          <span>{t('ViewShops.已选中x个站点', { x: selectedRowKeys.length })}</span>
          <Button type="link" onClick={() => setSelectedRowKeys([])}>
            {t('ViewShops.取消选择')}
          </Button>
        </Col>
        <Col>
          <Button type="default" onClick={() => onBatchChange(false)}>
            {t('ViewShops.批量关闭')}
          </Button>
          <Button type="primary" onClick={() => onBatchChange(true)}>
            {t('ViewShops.批量开启')}
          </Button>
        </Col>
      </Row>
    );
  };
  return (
    <>
      <div onClick={() => setVisible(true)}>{t('ViewShops.别名展示设置')}</div>
      <JustRightHeightModal
        visible={visible}
        centered
        onCancel={() => setVisible(false)}
        title={`${toLowerCase(platform)} ${t('ViewShops.别名展示设置')}`}
        footer={footer()}
        className={shopAliasSettingCls}
      >
        <Table
          dataSource={dataSource}
          columns={columns}
          pagination={false}
          rowSelection={rowSelection}
          rowKey="region"
        />
      </JustRightHeightModal>
    </>
  );
};

export default ShopAliasSetting;
